import { type DecisionExportFormat, DecisionExportFormat as Formats } from '@apm/contracts';
import { CircleAlert, FileCode2, FileText } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Dialog } from '../../../components/ui/dialog';
import { cn } from '../../../lib/utils';
import { stampDateTime } from '../model';

/** The previous export of one format, as the format card shows it. */
export interface ExportStamp {
  /** ISO — when the newest version of that document was written. */
  readonly at: string;
  readonly version: number;
}

const ICONS: Record<DecisionExportFormat, typeof FileText> = {
  chilldoc: FileText,
  md: FileCode2,
};

/**
 * "Xuất thành tài liệu dự án" — chọn định dạng (APM-DSX, người dùng
 * 2026-10-07). Hai thẻ lớn: `.chilldoc` (bảng định dạng, sửa được trong app) và
 * `.md` (đọc được ở mọi nơi). Mỗi định dạng là **một tài liệu dự án riêng**,
 * nên thẻ nào đã xuất thì nói lần xuất gần nhất của chính nó, kèm nút Mở.
 *
 * Nút xác nhận là nút chữ "Xuất", không phải ✓ của `DialogApply`: bấm là tạo
 * tài liệu / phiên bản mới trong dự án — cùng lý do với hộp "Ghi vào RAID".
 */
export function ExportFormatDialog({
  exports,
  pending,
  onExport,
  onOpenDocument,
  onClose,
}: {
  /** Lần xuất gần nhất của từng định dạng — thiếu = chưa xuất (hoặc tài liệu đã xoá). */
  readonly exports: Partial<Record<DecisionExportFormat, ExportStamp | undefined>>;
  readonly pending: boolean;
  readonly onExport: (format: DecisionExportFormat) => void;
  readonly onOpenDocument: (format: DecisionExportFormat) => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [format, setFormat] = useState<DecisionExportFormat>('chilldoc');

  return (
    <Dialog label={t('decisionSheet.exportDoc.dialogTitle')} onClose={onClose}>
      <h2 className="text-lg font-semibold">{t('decisionSheet.exportDoc.dialogTitle')}</h2>
      <p className="text-sm text-muted-foreground">{t('decisionSheet.exportDoc.dialogHint')}</p>

      <div
        role="radiogroup"
        aria-label={t('decisionSheet.exportDoc.dialogTitle')}
        className="grid gap-3 sm:grid-cols-2"
      >
        {Formats.options.map((option) => {
          const Icon = ICONS[option];
          const selected = option === format;
          const stamp = exports[option];
          return (
            <div
              key={option}
              className={cn(
                'flex flex-col gap-2 rounded-xl border p-3 transition-colors',
                selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border',
              )}
              data-testid={`export-format-${option}`}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="decision-export-format"
                  value={option}
                  checked={selected}
                  onChange={() => setFormat(option)}
                  className="mt-1 size-4 shrink-0 accent-primary"
                />
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    {t(`decisionSheet.exportDoc.formats.${option}.title`)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t(`decisionSheet.exportDoc.formats.${option}.hint`)}
                  </span>
                </span>
              </label>
              {stamp ? (
                <p
                  className="flex flex-wrap items-center gap-x-1.5 pl-7 text-xs text-muted-foreground"
                  data-testid={`export-format-${option}-stamp`}
                >
                  <span>
                    {t('decisionSheet.exportDoc.exportedAt', {
                      at: stampDateTime(stamp.at),
                      version: stamp.version,
                    })}
                  </span>
                  <span aria-hidden>·</span>
                  <button
                    type="button"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    onClick={() => onOpenDocument(option)}
                  >
                    {t('decisionSheet.exportDoc.openShort')}
                  </button>
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button disabled={pending} onClick={() => onExport(format)} data-testid="export-submit">
          {pending ? t('decisionSheet.exportDoc.exporting') : t('decisionSheet.exportDoc.submit')}
        </Button>
      </div>
    </Dialog>
  );
}

/**
 * "Chưa xuất được tài liệu" — sổ chưa đóng (APM-DSX, người dùng 2026-10-07).
 * Cùng khuôn với `OpenProblemsDialog`: một lý do, một nút đi sửa. Sổ `open` có
 * nút "Đóng sổ" (xong thì trang mở tiếp hộp chọn định dạng); sổ `draft` thì
 * chưa có gì để đóng — chỉ nói lý do.
 */
export function ExportNotClosedDialog({
  status,
  closing,
  onCloseSheet,
  onClose,
}: {
  readonly status: 'draft' | 'open';
  readonly closing: boolean;
  readonly onCloseSheet: () => void;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Dialog label={t('decisionSheet.exportDoc.notClosed.title')} onClose={onClose}>
      <h2 className="text-lg font-semibold">{t('decisionSheet.exportDoc.notClosed.title')}</h2>
      <div
        className="flex flex-col gap-2 rounded-xl border border-rag-amber/40 bg-rag-amber/5 p-3 sm:flex-row sm:items-center"
        data-testid="export-not-closed"
      >
        <span className="flex min-w-0 flex-1 items-start gap-2 text-sm">
          <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
          <span className="[overflow-wrap:anywhere]">
            {t(`decisionSheet.exportDoc.notClosed.${status}`)}
          </span>
        </span>
        {status === 'open' ? (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0 self-start sm:self-auto"
            disabled={closing}
            onClick={onCloseSheet}
          >
            {t('decisionSheet.exportDoc.notClosed.close')}
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
}
